import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { Box } from "@mui/material";
import { store } from "../../../store";
import {
  ClearAssetAction,
  DeleteAssetAction,
  GetAssetAction,
  GetTagsAction,
  getTagsSelector,
  getVODSelector,
  getVODsSelector,
  SetAssetAction,
  UpdateAssetAction,
  UpdateAssetEntitlementsAction,
} from "../../../store/reducers/cmsReducer";
import { CMSAsset } from "../../../../../models/CMS/cmsAsset";
import { DetailPageHeader } from "../../../ui-components/pageLayout/DetailPageHeader";
import LocalizedTextsAccordion from "../SharedAccordions/LocalizedTexts";
import LocalizedImagesAccordion from "../SharedAccordions/LocalizedImagesAccordion";
import MediaAccordion from "../SharedAccordions/MediaAccordion";
import BasicInformationAccordion from "./accordions/BasicInformationAccordion";
import TagsAccordion from "../SharedAccordions/TagsAccordion";
import GenresAccordion from "../SharedAccordions/GenresAccordion";
import MediasAccordion from "./accordions/MediasAccordion";
import SeriesAccordion from "./accordions/SeriesAccordion";
import { CMSTag } from "../../../../../models/CMS/cmsTag";
import { IngestPanel } from "./IngestPanel";
import EntitlementsAccordion from "../SharedAccordions/EntitlementsAccordion";

const UpdateAssetSchema = Yup.object().shape({
  originalTitle: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required*"),
  originalLanguage: Yup.string()
    .min(2, "Country code must be 2 letters")
    .max(2, "Country code must be 2 letters"),
  countryOfOrigin: Yup.string()
    .min(2, "Country code must be 2 letters")
    .max(2, "Country code must be 2 letters"),
  medias: Yup.array().of(Yup.object()).default([]),
});

export const VODPage: React.FC = () => {
  const assets = useSelector(getVODsSelector);
  const asset = useSelector(getVODSelector);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const history = useNavigate();
  const params = useParams();
  const location = useLocation();
  const stateSearch = (location.state as { listSearch?: unknown } | null)
    ?.listSearch;
  const listSearch =
    typeof stateSearch === "string" && stateSearch.startsWith("?")
      ? stateSearch
      : "";
  const tags = useSelector(getTagsSelector);
  const [providers, setProviders] = useState<CMSTag[]>([]);

  useEffect(() => {
    store.dispatch(GetTagsAction());
  }, []);

  useEffect(() => {
    // Filter tags to get only providers
    // Potentially use Provider instead of Tag in the future
    const providerTags = tags.filter((tag) => tag.getType() === "provider");
    setProviders(providerTags);
  }, [tags]);

  const redirect = useCallback(() => {
    history(`/admin/metadata/vod${listSearch}`);
  }, [history, listSearch]);

  useEffect(() => {
    if (!asset && !isDeleting && params.id) {
      const foundAsset = assets?.find(
        (asset) => asset.getUniqueId() === params.id
      );
      if (foundAsset) {
        store.dispatch(SetAssetAction(foundAsset.serialize()));
      } else {
        store.dispatch(GetAssetAction(params.id));
      }
    } else if (!asset && isDeleting) {
      redirect();
    }
  }, [assets, asset, isDeleting, params.id, redirect]);

  useEffect(() => {
    return () => {
      store.dispatch(ClearAssetAction());
    };
  }, []);

  const submitDeleteAsset = () => {
    setIsDeleting(true);
    if (params.id) {
      store.dispatch(DeleteAssetAction(params.id));
    } else {
      console.error("No asset id found for deletion.");
    }
  };

  const submitUpdateAsset = (values: any) => {
    const { entitlementCollectionIds, ...assetValues } = values;
    store.dispatch(UpdateAssetAction(new CMSAsset(assetValues).serialize()));
    if (entitlementCollectionIds) {
      store.dispatch(
        UpdateAssetEntitlementsAction(
          asset!.getUniqueId(),
          entitlementCollectionIds
        )
      );
    }
  };

  const [entitlementRefreshKey, setEntitlementRefreshKey] = useState(0);

  const refreshAsset = useCallback(() => {
    if (params.id) {
      store.dispatch(GetAssetAction(params.id));
      setEntitlementRefreshKey((k) => k + 1);
    }
  }, [params.id]);

  return (
    <>
      {asset && (
        <>
          <Formik
            initialValues={asset}
            enableReinitialize
            validationSchema={UpdateAssetSchema}
            onSubmit={(values) => {
              submitUpdateAsset(values);
            }}
          >
            {({ setFieldValue }) => (
              <Form>
                <DetailPageHeader
                  title="Asset Metadata"
                  onDelete={submitDeleteAsset}
                  entity={asset}
                  onBack={redirect}
                />
                <BasicInformationAccordion providers={providers} />
                <MediaAccordion asset={asset} />
                <LocalizedTextsAccordion />
                <LocalizedImagesAccordion
                  assetId={asset.getUniqueId()}
                  setFieldValue={setFieldValue}
                />
                <TagsAccordion />
                <GenresAccordion />
                <SeriesAccordion />
                <MediasAccordion
                  readonly={asset?.sourceType === "catchup"}
                  topSection={
                    asset?.sourceType !== "catchup" ? (
                      <Box sx={{ px: 2, py: 1 }}>
                        <IngestPanel
                          assetId={asset.getUniqueId()}
                          hasMedia={!!(asset.medias && asset.medias.length > 0)}
                          onIngestComplete={refreshAsset}
                        />
                      </Box>
                    ) : undefined
                  }
                />
                <EntitlementsAccordion
                  assetId={asset.getUniqueId()}
                  readonly={asset?.sourceType === "catchup"}
                  refreshKey={entitlementRefreshKey}
                />
              </Form>
            )}
          </Formik>
        </>
      )}
    </>
  );
};
